import { z } from 'zod';

export const TipoActividadEnum = z.enum([
  // 9 categorias formato legacy (TAREAS DE LA ACTIVIDAD)
  // — 8 originales + CONFECCION_ART (pedido cliente 2026-07)
  'TRABAJO_EFECTIVO',
  'TRASLADO_HACIA',
  'TRASLADO_DESDE',
  'CHARLA_SEGURIDAD',
  'VERIFICACION_ACCESO',
  'ESPERA_RECINTO',
  'ESPERA_AFECTADO',
  'BUSQUEDA_MATERIALES',
  'CONFECCION_ART',
  // Compatibilidad con registros existentes
  'PREVENTIVO',
  'CORRECTIVO',
  'INSPECCION',
  'OTRO',
]);
export type TipoActividad = z.infer<typeof TipoActividadEnum>;

export const TIPO_ACTIVIDAD_LABELS: Record<string, string> = {
  TRABAJO_EFECTIVO: '1. Trabajo Efectivo',
  TRASLADO_HACIA: '2. Traslado hacia lugar de trabajo',
  TRASLADO_DESDE: '3. Traslado desde lugar de trabajo',
  CHARLA_SEGURIDAD: '4. Charla de seguridad',
  VERIFICACION_ACCESO: '5. Verificacion de acceso',
  ESPERA_RECINTO: '6. Espera disponibilidad recinto',
  ESPERA_AFECTADO: '7. Espera disponibilidad afectado',
  BUSQUEDA_MATERIALES: '8. Busqueda de Materiales',
  CONFECCION_ART: '9. Confección de ART',
  PREVENTIVO: 'Preventivo (legacy)',
  CORRECTIVO: 'Correctivo (legacy)',
  INSPECCION: 'Inspeccion (legacy)',
  OTRO: 'Otro',
};

/** Las 9 categorias para el formato PDF "RECURSOS USADOS" (8 legacy + Confección de ART 2026-07) */
export const LEGACY_TOOLTIME_ROWS = [
  'TRABAJO_EFECTIVO',
  'TRASLADO_HACIA',
  'TRASLADO_DESDE',
  'CHARLA_SEGURIDAD',
  'VERIFICACION_ACCESO',
  'ESPERA_RECINTO',
  'ESPERA_AFECTADO',
  'BUSQUEDA_MATERIALES',
  'CONFECCION_ART',
] as const;

export const LEGACY_TOOLTIME_LABELS: Record<string, string> = {
  TRABAJO_EFECTIVO: 'Trabajo Efectivo',
  TRASLADO_HACIA: 'Traslado hacia el lugar de trabajo',
  TRASLADO_DESDE: 'Traslado desde el lugar de trabajo',
  CHARLA_SEGURIDAD: 'Charla de seguridad',
  VERIFICACION_ACCESO: 'Verificación de acceso',
  ESPERA_RECINTO: 'Espera por disponibilidad de recinto',
  ESPERA_AFECTADO: 'Espera por disponibilidad de afectado',
  BUSQUEDA_MATERIALES: 'Búsqueda de Materiales',
  CONFECCION_ART: 'Confección de ART',
};

/**
 * Maps every TipoActividad to whether it counts as "trabajo efectivo".
 * Mirrors the legacy `tipo_actividad_tooltime.efectivo` boolean flag,
 * which separated billable execution time from support activities
 * (esperas, traslados, charlas, etc.) in the legacy SIME stored procedure
 * `tooltimeKpi` (used by both Plan Matriz and Reportes Tooltime — options 1-5).
 *
 * `as const satisfies` keeps the literal types so `isEfectivo('TRABAJO_EFECTIVO')`
 * returns `true` (not `boolean`) and prevents mutation at runtime.
 */
export const TIPO_ACTIVIDAD_EFECTIVO_MAP = {
  TRABAJO_EFECTIVO: true,
  INSPECCION: true,
  PREVENTIVO: true,
  CORRECTIVO: true,
  TRASLADO_HACIA: false,
  TRASLADO_DESDE: false,
  CHARLA_SEGURIDAD: false,
  VERIFICACION_ACCESO: false,
  ESPERA_RECINTO: false,
  ESPERA_AFECTADO: false,
  BUSQUEDA_MATERIALES: false,
  CONFECCION_ART: false,
  OTRO: false,
} as const satisfies Record<TipoActividad, boolean>;

export function isEfectivo(t: TipoActividad | null | undefined): boolean {
  return t ? TIPO_ACTIVIDAD_EFECTIVO_MAP[t] : false;
}

export const CreateTooltimeSchema = z.object({
  inicio: z.string().datetime(),
  termino: z.string().datetime(),
  descripcion: z.string().max(1000).optional(),
  trabajadoresCount: z.number().int().min(1).optional(),
  tipoActividad: TipoActividadEnum.optional(),
  turno: z.enum(['A', 'B']).nullable().optional(),
  area: z.string().max(200).optional(),
  // When true, extending into later entries shifts them forward (cascade)
  // instead of rejecting the overlap. Opt-in so legacy callers keep the
  // strict "reject on overlap" behavior. See tooltime.service.ts.
  cascade: z.boolean().optional(),
}).refine(data => new Date(data.termino) > new Date(data.inicio), {
  message: 'Termino debe ser posterior a inicio',
  path: ['termino'],
});

export const UpdateTooltimeSchema = z.object({
  inicio: z.string().datetime().optional(),
  termino: z.string().datetime().optional(),
  descripcion: z.string().max(1000).optional(),
  trabajadoresCount: z.number().int().min(1).optional(),
  tipoActividad: TipoActividadEnum.optional(),
  turno: z.enum(['A', 'B']).nullable().optional(),
  area: z.string().max(200).optional(),
  // See CreateTooltimeSchema.cascade — when true, extending an entry shifts
  // later entries forward instead of rejecting the overlap.
  cascade: z.boolean().optional(),
}).refine(
  (data) => !(data.inicio && data.termino) || new Date(data.termino) > new Date(data.inicio),
  { message: 'Termino debe ser posterior a inicio', path: ['termino'] },
);

export type CreateTooltimeInput = z.infer<typeof CreateTooltimeSchema>;
export type UpdateTooltimeInput = z.infer<typeof UpdateTooltimeSchema>;
